import { client } from '@/api/client';

// Trip <-> Load linking helpers. The relationship is genuinely
// many-to-many (see 0034_trips.sql's own comment):
//   - one trip can carry several loads (LTL / multi-stop)
//   - one load can be relayed across several trips (multi-leg),
//     ordered by leg_sequence
// The generic client.entities system handles plain CRUD on Trip and
// TripLoad directly; this file only covers the join logic and the
// trip-number sequence, same division of labor as invoiceHelpers.js.

// Next sequential TRIP-#### number given the existing trips list.
// Malformed/missing trip_number values are ignored rather than
// crashing or skewing the max.
export function nextTripNumber(existingTrips) {
  const nums = (existingTrips || [])
    .map(t => parseInt((t.trip_number || '').replace('TRIP-', ''), 10))
    .filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1001;
  return `TRIP-${next}`;
}

// leg_sequence is "which leg of THIS LOAD's journey is this" -- so it's
// derived from how many other trips already carry this same load, not
// from anything about the trip being attached to.
export async function attachLoadToTrip(tripId, loadId) {
  const existingLegs = await client.entities.TripLoad.filter({ load_id: loadId });
  if (existingLegs.some(l => l.trip_id === tripId)) return existingLegs.find(l => l.trip_id === tripId);
  const legSequence = existingLegs.length + 1;
  const link = await client.entities.TripLoad.create({ trip_id: tripId, load_id: loadId, leg_sequence: legSequence });
  await syncTripSummaryFromLoads(tripId);
  return link;
}

export async function detachLoadFromTrip(tripLoadId, tripId) {
  await client.entities.TripLoad.delete(tripLoadId);
  if (tripId) await syncTripSummaryFromLoads(tripId);
}

// Loads attached to a trip, joined with their full Load row.
export async function listLoadsForTrip(tripId) {
  const links = await client.entities.TripLoad.filter({ trip_id: tripId });
  if (!links.length) return [];
  const loads = await Promise.all(links.map(l => client.entities.Load.get(l.load_id).catch(() => null)));
  return links.map((link, i) => ({ link, load: loads[i] })).filter(x => x.load);
}

// Trips a given load has been relayed across, ordered by leg.
export async function listTripsForLoad(loadId) {
  const links = await client.entities.TripLoad.filter({ load_id: loadId });
  if (!links.length) return [];
  const sorted = [...links].sort((a, b) => a.leg_sequence - b.leg_sequence);
  const trips = await Promise.all(sorted.map(l => client.entities.Trip.get(l.trip_id).catch(() => null)));
  return sorted.map((link, i) => ({ link, trip: trips[i] })).filter(x => x.trip);
}

// Re-derives a trip's denormalized origin/destination/pickup_date/
// delivery_date from its attached loads (earliest pickup -> latest
// delivery) so the Trips list can show a route without joining on
// every render. Empty Move / Broker trips with no loads attached keep
// whatever was entered manually on the trip itself.
export async function syncTripSummaryFromLoads(tripId) {
  const attached = await listLoadsForTrip(tripId);
  const loads = attached.map(a => a.load).filter(Boolean);
  if (loads.length === 0) return;

  const byPickup = [...loads].filter(l => l.pickup_date).sort((a, b) => a.pickup_date.localeCompare(b.pickup_date));
  const byDelivery = [...loads].filter(l => l.delivery_date).sort((a, b) => a.delivery_date.localeCompare(b.delivery_date));
  const firstPickupLoad = byPickup[0] || loads[0];
  const lastDeliveryLoad = byDelivery[byDelivery.length - 1] || loads[loads.length - 1];

  await client.entities.Trip.update(tripId, {
    origin: firstPickupLoad?.origin || '',
    destination: lastDeliveryLoad?.destination || '',
    pickup_date: byPickup[0]?.pickup_date || null,
    delivery_date: byDelivery[byDelivery.length - 1]?.delivery_date || null,
    // Facility names (e.g. "Jamie's Dairy Farms" -> "Walmart DC #736"),
    // not just city/state -- sourced from the load's own shipper/
    // consignee contact, which is already captured at booking.
    origin_name: firstPickupLoad?.shipper_name || '',
    destination_name: lastDeliveryLoad?.consignee_name || '',
  });
}

// Physical stop count for a set of loads on one trip: pickup + delivery
// per load, plus any additional_stops captured on that load (detention/
// multi-stop moves). Not deduped across loads sharing an exact stop
// (LTL consolidation picking up 2 loads at the same dock still counts
// 2 pickups here) -- close enough for a dispatch-facing count, and
// matches what a single-load trip with no extra stops shows: 2.
export function computeTripStops(loads) {
  return (loads || []).reduce((sum, l) => {
    let extra = 0;
    try { extra = (JSON.parse(l.additional_stops || '[]') || []).length; } catch { /* ignore */ }
    return sum + 2 + extra;
  }, 0);
}
